
// auth.service.ts
import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpHeaders} from '@angular/common/http';
import { MerchantService } from '../merchant.service';
import {LoginModel} from '../../../models/login.model';
import {BehaviorSubject, from, map, Observable, tap} from 'rxjs';
import GlobalComponent from '../../../global-component';
import {HttpRequestService} from '../http-request.service';
import {tokenSample} from '../../data/default.data';
import {RETRY_ON_AUTH_FAILURE} from './auth.interceptor';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private httpReq = inject(HttpRequestService);

  public accessToken: string | null = null;
  protected expiresIn: number | null = null;
  protected tokenType: string | null = null;
  private accessToken$ = new BehaviorSubject<string | null>(null);
  private authenticated = false;
  private tokenFetchPromise: Promise<string> | null = null;

  /** Prevent duplicate concurrent refresh calls */
  private refreshInFlight = false;
  private refreshPromise: Promise<string> | null = null;

  constructor() {}


  /**
   * For onboarding.component.ts (on init):
   * Fetch a fresh token and keep it in memory only.
   */
  async fetchInitialAccessToken(initPayload: Record<string, unknown> = {}): Promise<string> {
    const headers = new HttpHeaders({
      'Accept': 'application/json',
      // 'Content-Type': 'multipart/form-data', // Remove this for request body to work
    });
    const context = new HttpContext().set(RETRY_ON_AUTH_FAILURE, true);

    // Build multipart/form-data body
    const form = new FormData();
    form.append('grant_type', GlobalComponent.AUTH_GRANT_TYPE);
    form.append('client_id', GlobalComponent.AUTH_CLIENT_ID.toLowerCase());
    form.append('client_secret', GlobalComponent.AUTH_CLIENT_SECRET);
    form.append('scope', GlobalComponent.AUTH_CLIENT_SCOPE);

    // console.log('fetchInitialAccessToken:', [initPayload, form]);

    const resp: any = await this.httpReq.executeRequest(
      GlobalComponent.TOKEN_URL,
      headers,
      'post',
      undefined,
      form,
      context,
      true
    );

    const token = this.extractToken(resp);
    this.accessToken = token;
    this.expiresIn = (resp as any)?.expires_in ?? (resp as any)?.expiresIn ?? null;
    this.tokenType  = (resp as any)?.token_type  ?? (resp as any)?.tokenType  ?? null;
    // console.log('fetchInitialAccessToken (multipart):', [GlobalComponent.TOKEN_URL, token, this.expiresIn]);
    return token;
  }

  /** Read the current in‑memory token (never persisted). */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /** Allow onboarding.component.ts to inject/set a token it fetched elsewhere (still in memory only). */
  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  /** Clear token (e.g., on logout). */
  clearAccessToken(): void {
    this.accessToken = null;
  }

  /**
   * Lightweight auth check for guards/templates.
   * Current policy: authenticated if a token is present in memory.
   * (Optional stricter JWT-exp validation shown below.)
   */
  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  /**
   * Called by the functional auth interceptor on an auth failure.
   * Must return an Observable<string> so the interceptor can switchMap and retry once.
   * Uses HttpRequestService under the hood.
   */
  fetchNewAccessToken(refreshPayload: Record<string, unknown> = {}): Observable<string> {
    if (this.refreshInFlight && this.refreshPromise) {
      // Coalesce concurrent refresh attempts into the in‑flight one.
      return from(this.refreshPromise);
    }

    this.refreshInFlight = true;

    const headers = new HttpHeaders({
      Accept: 'application/json',
      // 'Content-Type': 'application/json',
    });

    // Build a single Promise so multiple subscribers share the same result.
    this.refreshPromise = this.httpReq
      .executeRequest(GlobalComponent.REFRESH_URL, headers, 'post', refreshPayload)
      .then((resp: any) => {
        const token = this.extractToken(resp);
        this.accessToken = token;
        return token;
      })
      .finally(() => {
        // Reset guard when the refresh completes (success or error)
        this.refreshInFlight = false;
        this.refreshPromise = null;
      });

    return from(this.refreshPromise);
  }

  /**
   * Normalize token extraction from your backend response.
   * Adjust if your API returns a different shape.
   */
  private extractToken(resp: any): string {
    // 1) Plain text token
    if (typeof resp === 'string') {
      const trimmed = resp.trim();
      return trimmed ? trimmed : '';
    }

    // 2) JSON object with common OAuth2 fields (snake_case)
    if (resp && typeof resp === 'object') {
      const obj = resp as any;
      return obj.access_token ?? obj.accessToken ?? null;
    }

    return '';
  }

  setToken(token: string, isReal = true): void {
    this.accessToken$.next(token);
    this.authenticated = isReal; // mark true only for real tokens
  }


  useFallbackToken(): void {
    // This token should only be used for dev/testing scenarios.
    this.setToken(tokenSample.token, /* isReal */ false);
    console.warn('[AuthService] Using fallback token');
  }
}

